import { isString, get, has, replace, pick, find } from 'lodash';
import clsx from 'clsx';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ErrorBoundary from '../../../../common/components/ErrorBoundary';
import hasAccess, { editChargerDetailsAccessId } from '../../../../common/services/HasAccess';
import { toTitleCase } from '../../../../common/services/I2P2Helper';
import { loginToCustomerFromCharger } from '../../../ChargersList/actions';
import { StatusLabel, yesNoChildren } from '../../../../common/constants/CommonUsedDropdownsStatics';
import { getOfflineTimeInfo } from '../../../../common/services/ChargerPreprocessor';
import { CHARGER_ONLINE_NO, CHARGER_ONLINE_YES } from '../../../../common/constants/statuses';

const mapChildValues = (children = []) =>
  children.reduce((arr, item) => {
    arr.push(isString(item) ? item : mapChildValues([item.props.children]).join(''));

    return arr;
  }, []);

const renderChildValues = ({ children }) => {
  const [result, rest] = children;

  return (
    <span>
      {result} <span className={rest.props.className}>{mapChildValues(rest.props.children)}</span>
    </span>
  );
};

export const renderReadOnlyTextField = (field, props) => {
  const { currentChargerData } = props;
  let value = field.isUnknownValue ? 'Unknown' : get(currentChargerData, field.name);

  if (field.titleCase) {
    value = value ? toTitleCase(value) : '';
  }

  if (has(value, 'props') && get(value, 'props.children')) {
    value = renderChildValues(value.props);
  }

  if (field.hidden) return null;

  return (
    <ErrorBoundary key={field.name} errorFallback={`Error in "${field.floatingLabelText}" field`}>
      <div className="staticField">
        <div className="label">
          {field.floatingLabelText}
          <div className={clsx(field.customStyle, 'value')}>{value}</div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export const renderReadOnlyString = (field, props) => {
  const { currentChargerData } = props;
  const value = get(currentChargerData, field.name);
  const visibleText = field.textToRender || `${field.floatingLabelText} = ${replace(value, / /g, '\u00A0')}`;

  return (
    <ErrorBoundary key={field.name} errorFallback={`Error in "${field.floatingLabelText}" field`}>
      <div className="staticField">
        <div className="value">{visibleText}</div>
      </div>
    </ErrorBoundary>
  );
};

export const CustomerChargerLinkField = ({ customer, user, chargerId }) => {
  const refreshToken = useSelector((state) => get(state, 'login.refreshToken'));
  const dispatch = useDispatch();

  const link = hasAccess(editChargerDetailsAccessId, user.currentPermissions)
    ? `/chargers/edit/${chargerId}/details/`
    : `/chargers/details/${chargerId}/details/`;

  if (!customer.customerId || !customer.url || !customer.customerName) {
    return null;
  }

  return (
    <Link
      to="#"
      onClick={() => {
        dispatch(
          loginToCustomerFromCharger({
            customerURL: customer.url,
            customerId: customer.customerId,
            refreshToken,
            chargerURL: link,
          }),
        );
      }}
    >
      {customer.customerName}
    </Link>
  );
};

export const renderCustomerNameField = (field, props) => {
  const { currentChargerData, user } = props;
  const chargerId = get(currentChargerData, 'c_uuid');
  const customer = get(currentChargerData, 'customer', {});

  return (
    <ErrorBoundary key={field.name} errorFallback={`Error in "${field.floatingLabelText}" field`}>
      <div key={field.name} className="staticField">
        <div className="label">
          {field.floatingLabelText}
          <div className={clsx(field.customStyle, 'value')}>
            {user.customer_id === customer.customerId ? (
              customer.customerName
            ) : (
              <CustomerChargerLinkField customer={customer} chargerId={chargerId} user={user} />
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export const renderOnlineComputedField = (field, props) => {
  function getOnlineValue(charger) {
    const {
      i2p2_state: { c_online: online, c_online_ts: cOnlineTs },
    } = pick(charger, ['i2p2_state.c_online', 'i2p2_state.c_online_ts']);
    let onlineComputed = '';

    if (undefined !== online && online !== null) {
      const status = find(yesNoChildren, (o) => o.value === online);

      onlineComputed = <StatusLabel status={status.value ? CHARGER_ONLINE_YES : CHARGER_ONLINE_NO} />;
    }

    if (cOnlineTs) {
      onlineComputed = (
        <span>
          {onlineComputed}
          <span className="smaller">
            &nbsp;(
            {getOfflineTimeInfo(cOnlineTs)})
          </span>
        </span>
      );
    }

    return onlineComputed;
  }

  return renderReadOnlyTextField(field, {
    currentChargerData: {
      [field.name]: getOnlineValue(props.currentChargerData),
    },
  });
};
